import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { currencyExponent, minorToMajor } from '@/shared/lib/money'
import { CONTRACT_KINDS, type Contract, type PersonDetails, type SubscriberPlace } from './contractApi'
import { CONTRACT_PRINT_CSS, CONTRACT_PRINT_ROOT } from './contractPrint'

export interface ContractPrintData {
  contract: Contract
  person: PersonDetails
  place: SubscriberPlace
  subscriber: { customerId: string | null; fullName: string; fatherName: string | null; phone: string | null }
  terms: string | null
  letterheadUrl: string | null
  companyName: string | null
}

const PRINT_LANGUAGE = 'fa'

const cell: CSSProperties = { border: '0.25mm solid #444', padding: '1mm 1.6mm', verticalAlign: 'top' }
const labelCell: CSSProperties = { ...cell, background: '#eef3f6', width: '24%', fontWeight: 600, whiteSpace: 'nowrap' }
const sectionTitle: CSSProperties = { fontWeight: 700, fontSize: '10pt', margin: '2.2mm 0 1mm' }

function Grid({ rows }: { rows: ReadonlyArray<readonly [string, ReactNode, string, ReactNode]> }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <tbody>
        {rows.map(([l1, v1, l2, v2]) => (
          <tr key={l1 + l2}>
            <td style={labelCell}>{l1}</td>
            <td style={cell}>{v1}</td>
            <td style={labelCell}>{l2}</td>
            <td style={cell}>{v2}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Shartnomaning bitta A4 sahifasi — blanka fon rasmi ustida, dariy tilida (ADR-051). */
export function ContractSheet({ data }: { data: ContractPrintData }) {
  const { i18n } = useTranslation()
  const t = i18n.getFixedT(PRINT_LANGUAGE)
  const { contract, person, place, subscriber } = data
  const digits = currencyExponent(contract.currency)
  const amountFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: digits })
  const money = (value: number | null | undefined) =>
    value == null ? '' : `${amountFormat.format(minorToMajor(value, contract.currency))} ${contract.currency}`
  const legal = person.personKind === 'LEGAL'
  const guarantor = contract.kind === 'AMANAT' || contract.kind === 'RENTAL' || contract.guarantorName

  return (
    <div
      dir="rtl"
      style={{
        position: 'relative',
        width: '210mm',
        height: '297mm',
        overflow: 'hidden',
        background: '#fff',
        color: '#000',
        fontFamily: 'Tahoma, "DejaVu Sans", Arial, sans-serif',
        fontSize: '8.6pt',
        lineHeight: 1.35,
      }}
    >
      {data.letterheadUrl ? (
        <img
          src={data.letterheadUrl}
          alt=""
          style={{ position: 'absolute', inset: 0, width: '210mm', height: '297mm', objectFit: 'fill', zIndex: 0 }}
        />
      ) : null}
      <div style={{ position: 'relative', zIndex: 1, padding: '34mm 13mm 24mm' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2mm' }}>
          <div style={{ fontSize: '13pt', fontWeight: 700 }}>{t('contract.print.title')}</div>
          <div>
            {t('contract.print.number')}: <bdi style={{ fontWeight: 700 }}>{contract.number}</bdi>
          </div>
        </div>
        {data.companyName ? <div style={{ fontWeight: 600, marginBottom: '1mm' }}>{data.companyName}</div> : null}

        <div style={sectionTitle}>{t('contract.print.customer')}</div>
        <Grid
          rows={[
            [t('contract.print.customerId'), <bdi>{subscriber.customerId ?? ''}</bdi>, t('contract.print.personKind'),
              legal ? t('contract.print.legal') : t('contract.print.individual')],
            [t('contract.print.startsOn'), <bdi>{contract.startsOn}</bdi>, t('contract.print.endsOn'), <bdi>{contract.endsOn}</bdi>],
            [t('contract.print.fullName'), subscriber.fullName, t('contract.print.fatherName'), subscriber.fatherName ?? ''],
            [t('contract.print.nationalId'), <bdi>{person.nationalId ?? ''}</bdi>, t('contract.print.occupation'), person.occupation ?? ''],
            [t('contract.print.education'), person.education ?? '', t('contract.print.phone'), <bdi>{subscriber.phone ?? ''}</bdi>],
            ...(legal
              ? ([
                  [t('contract.print.companyName'), person.companyName ?? '', t('contract.print.licenseNumber'),
                    <bdi>{person.licenseNumber ?? ''}</bdi>],
                  [t('contract.print.directorName'), person.directorName ?? '', t('contract.print.signerPosition'),
                    person.signerPosition ?? ''],
                ] as const)
              : []),
            [t('contract.print.familyPhone'), <bdi>{person.familyPhone ?? ''}</bdi>, t('contract.print.province'), place.regionName ?? ''],
            [t('contract.print.district'), place.districtName ?? '', t('contract.print.village'), person.installVillage ?? ''],
            [t('contract.print.street'), person.installStreet ?? '', t('contract.print.house'), person.installHouse ?? ''],
          ]}
        />

        <div style={sectionTitle}>{t('contract.print.contract')}</div>
        <div style={{ display: 'flex', gap: '5mm', marginBottom: '1.2mm', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600 }}>{t('contract.print.kind')}:</span>
          {CONTRACT_KINDS.map((kind) => (
            <span key={kind}>
              {kind === contract.kind ? '☑' : '☐'} {t(`contract.kind.${kind}`)}
            </span>
          ))}
        </div>
        <Grid
          rows={[
            [t('contract.print.receiverDevice'), contract.receiverDevice ?? '', t('contract.print.routerType'), contract.routerType ?? ''],
            [t('contract.print.deviceMac'), <bdi>{contract.deviceMac ?? ''}</bdi>, t('contract.print.cableLength'),
              contract.cableLengthM == null ? '' : <bdi>{contract.cableLengthM} m</bdi>],
            [t('contract.print.packagePrice'), <bdi>{money(contract.packagePrice)}</bdi>, t('contract.print.routerPrice'),
              <bdi>{money(contract.routerPrice)}</bdi>],
            [t('contract.print.devicePrice'), <bdi>{money(contract.devicePrice)}</bdi>, t('contract.print.cablePrice'),
              <bdi>{money(contract.cablePrice)}</bdi>],
            [t('contract.print.installationTotal'), <bdi>{money(contract.installationTotal)}</bdi>, t('contract.print.paid'),
              <bdi>{money(contract.paidAmount)}</bdi>],
            [t('contract.print.remaining'), <bdi>{money(contract.remainingAmount)}</bdi>,
              contract.kind === 'RENTAL' ? t('contract.print.monthlyRental') : t('contract.print.deposit'),
              <bdi>{money(contract.kind === 'RENTAL' ? contract.monthlyRental : contract.depositAmount)}</bdi>],
          ]}
        />
        {contract.note ? (
          <div style={{ marginTop: '1mm' }}>
            <span style={{ fontWeight: 600 }}>{t('contract.print.note')}:</span> {contract.note}
          </div>
        ) : null}

        {data.terms ? (
          <>
            <div style={sectionTitle}>{t('contract.print.terms')}</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '7.8pt', lineHeight: 1.4, textAlign: 'justify' }}>{data.terms}</div>
          </>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '12mm', marginTop: '6mm' }}>
          {[t('contract.print.companySign'), t('contract.print.customerThumb')].map((caption) => (
            <div key={caption} style={{ textAlign: 'center' }}>
              <div style={{ height: '13mm', borderBottom: '0.25mm solid #000' }} />
              <div style={{ marginTop: '1mm', fontWeight: 600 }}>{caption}</div>
            </div>
          ))}
        </div>
        {guarantor ? (
          <div style={{ marginTop: '4mm' }}>
            <div style={{ fontWeight: 600, marginBottom: '1mm' }}>{t('contract.print.guarantor')}</div>
            <Grid
              rows={[
                [t('contract.print.guarantorName'), contract.guarantorName ?? '', t('contract.print.guarantorRelation'),
                  contract.guarantorRelation ?? ''],
                [t('contract.print.guarantorPhone'), <bdi>{contract.guarantorPhone ?? ''}</bdi>, t('contract.print.guarantorThumb'), ''],
              ]}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function ContractPrintPortal({ data }: { data: ContractPrintData }) {
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = CONTRACT_PRINT_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return createPortal(
    <div className={CONTRACT_PRINT_ROOT}>
      <ContractSheet data={data} />
    </div>,
    document.body,
  )
}
